import fetch from "node-fetch";

import CONFIG from "config/config";

import { authorization } from "module/auth";

import {
  createGraphQLAbortError,
  createGraphQLTimeoutError,
  isGraphQLAbortError,
  isGraphQLTimeoutError,
  resolveGraphQLTimeoutMs,
  GRAPHQL_ABORT_ERROR_NAME,
  GRAPHQL_TIMEOUT_ERROR_NAME,
} from "./timeout";

/**
 * Default ceiling for a single GraphQL round trip.
 *
 * Without this every call was an unbounded `await`: a stalled socket or a
 * backgrounded tab left the caller's promise pending forever, which is how the
 * EDDM map's "Loading distribution areas" spinner could stay up with no error
 * and no way back except a page reload.
 *
 * This ceiling is deliberately GLOBAL — an unbounded promise is a defect class,
 * not an EDDM quirk, and all 28 modules calling `callAuthorizedGraphQLEndpoint`
 * are equally exposed to it. It is set at 60s rather than a tighter interactive
 * number precisely so that the heavy non-EDDM callers (flyer-builder autosave
 * with a large document, campaign duplication) keep comfortable headroom: 60s
 * is far past any request a user is still waiting on, so it can only ever fire
 * on a request that was already lost. Individual callers can override with
 * `fetchOptions.timeoutMs`, and `timeoutMs: 0` disables the timeout entirely.
 *
 * Override globally with `REACT_APP_GRAPHQL_TIMEOUT_MS` (0 disables).
 */
export const DEFAULT_GRAPHQL_TIMEOUT_MS = resolveGraphQLTimeoutMs(process.env.REACT_APP_GRAPHQL_TIMEOUT_MS);

// Error identity lives in ./timeout (dependency-free) so the "a cancel is not a
// timeout" rule can actually be executed by scripts/test-eddm-route-walk.mjs;
// re-exported here because 28 modules import it from this path.
export {
  createGraphQLAbortError,
  createGraphQLTimeoutError,
  isGraphQLAbortError,
  isGraphQLTimeoutError,
  GRAPHQL_ABORT_ERROR_NAME,
  GRAPHQL_TIMEOUT_ERROR_NAME,
};

/**
 * Call Graph QL Endpoint
 * @param {string} query
 * @param {object} variables
 * @param {string} gqlConfigKey
 * @returns {Promise<object>}
 */
export const callGraphQLEndpoint = function (authToken, query, variables, graphQLEndpoint, fetchOptions = {}) {
  const body = JSON.stringify({
    query,
    variables,
  });

  const { timeoutMs = DEFAULT_GRAPHQL_TIMEOUT_MS, signal: callerSignal, ...restFetchOptions } = fetchOptions;

  return new Promise((resolve, reject) => {
    // Abort the request itself (not just the promise) so a stalled connection
    // releases the socket instead of sitting in the browser's request queue.
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    let timer = null;
    let settled = false;
    let abortListener = null;

    const cleanup = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (callerSignal && abortListener) {
        callerSignal.removeEventListener("abort", abortListener);
        abortListener = null;
      }
    };
    // Every exit below goes through these, so the promise can never be left
    // pending — that was the defect behind the endless EDDM map spinner.
    const settleResolve = (value) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(value);
    };
    const settleReject = (error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    if (callerSignal) {
      // A caller-supplied signal means somebody deliberately cancelled (the map's
      // Cancel control). Rejecting with a TIMEOUT error here is what made a
      // deliberate cancel surface to the caller — and to the user — as "it timed
      // out"; it now gets its own error identity.
      abortListener = () => {
        if (controller) controller.abort();
        settleReject(createGraphQLAbortError());
      };
      if (callerSignal.aborted) {
        abortListener();
        return;
      }
      callerSignal.addEventListener("abort", abortListener);
    }

    if (timeoutMs > 0) {
      timer = setTimeout(() => {
        if (controller) controller.abort();
        settleReject(createGraphQLTimeoutError(timeoutMs));
      }, timeoutMs);
    }

    fetch(graphQLEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: "Bearer " + authToken } : {}),
      },
      body,
      ...(controller ? { signal: controller.signal } : {}),
      ...restFetchOptions,
    })
      .then(async (response) => {
        const result = await response.clone();
        const jsonResult = await result.json();
        if (jsonResult.errors) {
          const error = jsonResult.errors[0];

          if (
            error.message == "Failed to authenticate token" ||
            error.message == "Token expired. Please login again." ||
            error.message.includes("Unauthorized") ||
            error.message.includes("Authentication")
          ) {
            const isPublicRoute = window.location.pathname.startsWith("/design/");
            const isInIframe = window.self !== window.top;
            if (isPublicRoute || isInIframe) {
              console.warn("Authentication error on public route/iframe, skipping redirect:", error.message);
              settleReject(error);
              return;
            }
            console.warn("Authentication error detected, logging out user:", error.message);
            // Settle BEFORE navigating: this branch used to return without
            // resolving or rejecting, leaving every caller's promise pending.
            settleReject(error);
            authorization.logout();
            // Use location.replace to prevent back button issues
            window.location.replace("/login");
            return;
          }
          settleReject(error);
        } else {
          settleResolve(response);
        }
      })
      .catch((error) => {
        if (error && error.name === "AbortError") {
          // Only reachable when the abort came from our own deadline: a caller
          // cancel has already settled the promise above.
          settleReject(
            callerSignal && callerSignal.aborted ? createGraphQLAbortError() : createGraphQLTimeoutError(timeoutMs)
          );
          return;
        }
        console.error("GraphQL network error:", error);
        settleReject(error);
      });
  });
};

export const callAuthorizedGraphQLEndpoint = (
  query,
  variables,
  graphQLEndpoint = CONFIG.GRAPHQL_API_BASE_URL,
  fetchOptions
) => {
  const authToken = authorization.getAPIKey();

  return callGraphQLEndpoint(authToken, query, variables, graphQLEndpoint, fetchOptions);
};

export const callNoAuthorizedGraphQLEndpoint = (query, variables, graphQLEndpoint = CONFIG.GRAPHQL_API_BASE_URL) => {
  return callGraphQLEndpoint("", query, variables, graphQLEndpoint);
};
